import axios from "./axios";
import {
  ENABLE_LINKEDIN_REGISTRATION_CONVERSION,
  LINKEDIN_PARTNER_ID,
  LINKEDIN_REGISTRATION_CONVERSION_ID,
} from "../constants";

type LintrkFunction = {
  (action: "track", payload: { conversion_id: number }): void;
  q?: Array<[string, unknown]>;
};

declare global {
  interface Window {
    lintrk?: LintrkFunction;
    _linkedin_data_partner_ids?: string[];
    _linkedin_partner_id?: string;
  }
}

const LINKEDIN_INSIGHT_SCRIPT_ID = "linkedin-insight-tag";
const claimedUserIds = new Set<string>();

const getConversionId = () => {
  const conversionId = Number(LINKEDIN_REGISTRATION_CONVERSION_ID);
  return Number.isFinite(conversionId) && conversionId > 0 ? conversionId : null;
};

const ensureLinkedInInsightTag = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (!ENABLE_LINKEDIN_REGISTRATION_CONVERSION) return false;
  if (!LINKEDIN_PARTNER_ID) return false;

  if (!window.lintrk) {
    const queuedLintrk: LintrkFunction = ((action, payload) => {
      queuedLintrk.q = queuedLintrk.q || [];
      queuedLintrk.q.push([action, payload]);
    }) as LintrkFunction;
    queuedLintrk.q = [];
    window.lintrk = queuedLintrk;
  }

  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  if (!window._linkedin_data_partner_ids.includes(LINKEDIN_PARTNER_ID)) {
    window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  }

  if (!document.getElementById(LINKEDIN_INSIGHT_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = LINKEDIN_INSIGHT_SCRIPT_ID;
    script.type = "text/javascript";
    script.async = true;
    script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }

  return typeof window.lintrk === "function";
};

export const trackLinkedInRegistrationConversionIfNeeded = async (userId?: string) => {
  const normalizedUserId = String(userId || "").trim();
  const conversionId = getConversionId();

  if (!normalizedUserId || !conversionId) return;
  if (!ensureLinkedInInsightTag()) return;
  if (claimedUserIds.has(normalizedUserId)) return;

  claimedUserIds.add(normalizedUserId);

  try {
    const { data } = await axios.post("/owner/linkedin-registration-conversion/claim", {
      userId: normalizedUserId,
    });

    if (!data?.shouldTrack) return;

    window.lintrk?.("track", { conversion_id: conversionId });
  } catch (error) {
    claimedUserIds.delete(normalizedUserId);
    if (process.env.NODE_ENV !== "production") {
      console.warn("[linkedin] Registration conversion tracking failed", error);
    }
  }
};
